import React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { marketingLogos, marketingPalette, marketingSymbols } from '../../content/marketing';

export const publicHeadingFontFamily = '"Sora", "Manrope", sans-serif';
export const publicBodyFontFamily = '"Manrope", "Segoe UI", sans-serif';
export const publicPalette = marketingPalette;

export const publicPrimaryButtonSx = {
  minHeight: 52,
  px: 3,
  bgcolor: publicPalette.heroBlueDeep,
  color: publicPalette.white,
  textTransform: 'none',
  fontWeight: 800,
  fontFamily: publicBodyFontFamily,
  borderRadius: '999px',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: publicPalette.heroBlueDeeper,
    boxShadow: 'none',
  },
};

export const publicSecondaryButtonSx = {
  minHeight: 52,
  px: 3,
  color: publicPalette.heroBlueDeep,
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: publicBodyFontFamily,
  borderRadius: '999px',
  borderColor: alpha(publicPalette.heroBlueDeep, 0.18),
  '&:hover': {
    borderColor: alpha(publicPalette.heroBlueDeep, 0.36),
    bgcolor: alpha(publicPalette.heroBlueDeep, 0.03),
  },
};

export const publicTextFieldSx = {
  '& .MuiInputLabel-root': {
    color: publicPalette.inkSoft,
    fontFamily: publicBodyFontFamily,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '18px',
    bgcolor: alpha('#ffffff', 0.96),
    fontFamily: publicBodyFontFamily,
    '& fieldset': {
      borderColor: alpha(publicPalette.ink, 0.10),
    },
    '&:hover fieldset': {
      borderColor: alpha(publicPalette.heroBlueMid, 0.36),
    },
    '&.Mui-focused fieldset': {
      borderColor: publicPalette.heroBlueMid,
      borderWidth: '1px',
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: publicBodyFontFamily,
  },
};

export const publicTabSx = {
  minHeight: 48,
  textTransform: 'none',
  fontWeight: 800,
  fontFamily: publicBodyFontFamily,
};

export const publicContentCardSx = {
  borderRadius: { xs: '28px', md: '34px' },
  p: { xs: 3, sm: 4, md: 4.5 },
  bgcolor: alpha('#ffffff', 0.90),
  border: `1px solid ${alpha(publicPalette.ink, 0.08)}`,
  boxShadow: '0 28px 70px rgba(18, 61, 103, 0.08)',
  backdropFilter: 'blur(14px)',
};

export const publicInfoSurfaceSx = {
  borderRadius: '24px',
  p: 2.5,
  bgcolor: alpha('#ffffff', 0.10),
  border: `1px solid ${alpha('#ffffff', 0.12)}`,
};

export const publicInlineLinkSx = {
  color: publicPalette.heroBlueMid,
  textDecoration: 'none',
  fontWeight: 700,
  '&:hover': {
    color: publicPalette.heroBlueDeep,
    textDecoration: 'underline',
  },
};

export function PublicPageFrame({ aside, children, containerMaxWidth, contentMaxWidth }) {
  const hasAside = Boolean(aside);
  const resolvedContainerMaxWidth = containerMaxWidth || (hasAside ? 'xl' : 'md');
  const resolvedContentMaxWidth = contentMaxWidth || (hasAside ? null : 760);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100%',
        bgcolor: publicPalette.heroBlueMist,
        background:
          `radial-gradient(circle at top left, ${alpha(publicPalette.heroBlue, 0.18)}, transparent 32%), linear-gradient(180deg, #eef8ff 0%, #f8fcff 48%, #f3faff 100%)`,
        py: { xs: 4, md: 6 },
      }}
    >
      <Box
        component="img"
        src={marketingSymbols.silhouette}
        alt=""
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 40,
          right: '10%',
          width: { lg: 220, xl: 280 },
          maxWidth: '20vw',
          opacity: 0.16,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth={resolvedContainerMaxWidth} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {hasAside ? (
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: { xs: '28px', md: '34px' },
                  p: { xs: 3, sm: 4, md: 4.5 },
                  background:
                    'linear-gradient(155deg, rgba(18, 61, 103, 0.98) 0%, rgba(24, 84, 132, 0.96) 58%, rgba(41, 145, 194, 0.92) 100%)',
                  color: publicPalette.white,
                  boxShadow: '0 34px 90px rgba(18, 61, 103, 0.16)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 'auto -12% -20% auto',
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    background: alpha('#ffffff', 0.12),
                    filter: 'blur(8px)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={marketingLogos.lockupLight}
                  alt="Virtual Medic"
                  sx={{
                    width: { xs: '100%', sm: 300 },
                    maxWidth: '100%',
                    mb: 3,
                  }}
                />
                {aside}
              </Box>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Box sx={publicContentCardSx}>{children}</Box>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              ...publicContentCardSx,
              width: '100%',
              maxWidth: resolvedContentMaxWidth,
              mx: 'auto',
            }}
          >
            {children}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export function PublicCardHeader({ eyebrow, title, description, compact = false }) {
  return (
    <Box sx={{ mb: compact ? 3 : 4 }}>
      <Box
        component="img"
        src={marketingLogos.compactDark}
        alt="Virtual Medic"
        sx={{
          width: { xs: 126, sm: 142 },
          maxWidth: '100%',
          mb: 2.5,
        }}
      />
      {eyebrow && (
        <Typography
          sx={{
            mb: 1,
            color: publicPalette.heroBlueMid,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.78rem',
            fontFamily: publicBodyFontFamily,
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        component="h1"
        sx={{
          mb: 1.25,
          color: publicPalette.ink,
          fontFamily: publicHeadingFontFamily,
          fontSize: compact ? { xs: '1.9rem', md: '2.2rem' } : { xs: '2rem', md: '2.45rem' },
          lineHeight: 1.08,
          letterSpacing: '-0.03em',
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            color: publicPalette.inkSoft,
            lineHeight: 1.72,
            fontFamily: publicBodyFontFamily,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}
